import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

interface ColumnMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (latColumn: string, lngColumn: string) => void;
  headers: string[];
  sampleData: any[];
}

const ColumnMappingDialog: React.FC<ColumnMappingDialogProps> = ({
  open,
  onClose,
  onConfirm,
  headers,
  sampleData
}) => {
  const [selectedLatColumn, setSelectedLatColumn] = useState<string>('');
  const [selectedLngColumn, setSelectedLngColumn] = useState<string>('');

  const handleLatColumnChange = (event: SelectChangeEvent) => {
    setSelectedLatColumn(event.target.value);
  };

  const handleLngColumnChange = (event: SelectChangeEvent) => {
    setSelectedLngColumn(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedLatColumn && selectedLngColumn) {
      onConfirm(selectedLatColumn, selectedLngColumn);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedLatColumn('');
    setSelectedLngColumn('');
    onClose();
  };

  const canConfirm = selectedLatColumn && selectedLngColumn && selectedLatColumn !== selectedLngColumn;

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'rgba(24, 29, 38, 0.95)',
          backdropFilter: 'blur(15px)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        Map CSV Columns
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          We couldn't automatically detect the latitude and longitude columns. 
          Please manually select which columns contain your coordinate data.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel 
              sx={{ 
                color: '#fff',
                '&.Mui-focused': { color: '#2c8fdb' }
              }}
            >
              Latitude Column
            </InputLabel>
            <Select
              value={selectedLatColumn}
              onChange={handleLatColumnChange}
              label="Latitude Column"
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                },
                '& .MuiSvgIcon-root': {
                  color: '#fff',
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(24, 29, 38, 0.95)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    '& .MuiMenuItem-root': {
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(44, 143, 219, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(44, 143, 219, 0.2)',
                      }
                    }
                  }
                }
              }}
            >
              {headers.map((header) => (
                <MenuItem key={header} value={header}>
                  {header}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel 
              sx={{ 
                color: '#fff',
                '&.Mui-focused': { color: '#2c8fdb' }
              }}
            >
              Longitude Column
            </InputLabel>
            <Select
              value={selectedLngColumn}
              onChange={handleLngColumnChange}
              label="Longitude Column"
              sx={{
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2c8fdb',
                },
                '& .MuiSvgIcon-root': {
                  color: '#fff',
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(24, 29, 38, 0.95)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    '& .MuiMenuItem-root': {
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(44, 143, 219, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(44, 143, 219, 0.2)',
                      }
                    }
                  }
                }
              }}
            >
              {headers.map((header) => (
                <MenuItem key={header} value={header}>
                  {header}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
          Preview (first 5 rows):
        </Typography>
        
        <TableContainer 
          component={Paper} 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            maxHeight: 300,
            '& .MuiTableCell-root': {
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.12)',
            }
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell 
                    key={header}
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: header === selectedLatColumn ? 'rgba(44, 143, 219, 0.2)' : 
                                      header === selectedLngColumn ? 'rgba(44, 143, 219, 0.2)' : 
                                      'transparent'
                    }}
                  >
                    {header}
                    {header === selectedLatColumn && ' (Lat)'}
                    {header === selectedLngColumn && ' (Lng)'}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sampleData.slice(0, 5).map((row, index) => (
                <TableRow key={index}>
                  {headers.map((header) => (
                    <TableCell key={header}>
                      {row[header] || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.12)', pt: 2 }}>
        <Button 
          onClick={handleCancel}
          sx={{ color: '#fff' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          disabled={!canConfirm}
          variant="contained"
          sx={{ 
            backgroundColor: '#2c8fdb',
            '&:hover': { backgroundColor: '#1976d2' },
            '&:disabled': { backgroundColor: 'rgba(255,255,255,0.12)' }
          }}
        >
          Import Data
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnMappingDialog;